import { Button, Combobox, CopyButton, Group, Input, InputBase, MantineSize, NumberInput, Select, SelectProps, Skeleton, Tooltip, useCombobox } from "@mantine/core";
import { IconCash, IconCurrencyDollar, IconHash, IconPhone, IconUser, IconUsersGroup, IconUsers, IconUserShare, IconArrowsExchange, IconBuildingBank, IconCreditCard, IconBrandPaypal, IconBrandStripe, IconWallet, IconCheck, IconId, IconAlertTriangle, IconMail, IconEmailStamp, IconUserCog } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { formatPrice, sortPersons } from "@/lib/format-utils";
import { resolveBookingPaxGroupPersons } from "@/lib/models-utils";
import { forwardRef, useState } from "react";
import {
    getCurrencies,
    getPaymentMethods,
    PAX_CATEGORY_COLORS,
    GroupSizeBooking,
    Person,
    Reservation,
    ServiceBookingPaxGroup,
    ServiceOrderPaxGroup,
    PERSON_CATEGORIES,
} from "@turizi/shared";
import { localTenant } from "@/services/session-service";
import { TagIcon } from "@/lib/tag-utils";


export const IdButton = ({
    id,
    size = "md"
}: {
    id: number | string;
    size?: "xs" | "sm" | "md" | "lg";
}) => {
    return (
        <CopyButton value={id?.toString() || ''}>
            {({ copied, copy }) => (
                <Tooltip label={copied ? 'copied!' : 'click to copy'}>
                    <Button
                        variant='light'
                        radius='md'
                        p={0}
                        color={copied ? 'teal' : 'black'}
                        onClick={(e) => {
                            e.stopPropagation();
                            copy();
                        }}
                    >
                        <div className={`flex items-center gap-1 text-${size} px-3`}>
                            <IconHash size={14} stroke={1} />
                            {id?.toString() || ''}
                        </div>
                    </Button>
                </Tooltip>
            )}
        </CopyButton>
    );
};


export const PriceCurrencyInput = ({
    label,
    value,
    color,
    onChange,
    value2,
    onChange2,
    className,
    icon,
    disabled = false
}: {
    label?: string;
    value: number;
    color?: string;
    onChange: (value: number) => void;
    value2: string;
    onChange2: (value: string) => void;
    className?: string;
    icon?: React.ReactNode;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <div className={className}>
            <div className="flex">
                <div>
                    {label && <label>{label}</label>}
                    <NumberInput
                        className="font-semibold"
                        disabled={disabled}
                        value={value}
                        size="sm"
                        thousandSeparator="."
                        decimalSeparator=","
                        hideControls
                        leftSection={icon || <IconCurrencyDollar size={16} stroke={2} color={color} />}
                        onChange={(value) => onChange(Number(value))}
                        styles={{
                            input: {
                                borderRight: 'none',
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                textAlign: 'right'
                            }
                        }}
                    />
                </div>
                <div>
                    {label && <label style={{ color: 'transparent' }}>.</label>}
                    <Select
                        size="sm"
                        value={value2}
                        onChange={(value) => {
                            if (value) onChange2(value);
                        }}
                        data={getCurrencies(t)}
                        styles={{
                            input: {
                                backgroundColor: '#f6f6f6',
                                borderLeft: 'none',
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0
                            }
                        }}
                    />
                </div>
            </div>
        </div >
    );
};

// infants do NOT occupy space
export const BookingPaxsSize = (booking: GroupSizeBooking) => (
    (booking.adults || 0) + (booking.children || 0) + (booking.seniors || 0) + (booking.guests || 0)
)


export const ShowPaxGroup = ({
    adults = 0,
    children = 0,
    seniors = 0,
    infants = 0,
    guests = 0,
    className = '',
    originalBooking,
    size = 'sm',
    showRetired = false,
}: {
    adults?: number;
    children?: number;
    seniors?: number;
    infants?: number;
    guests?: number;
    className?: string;
    originalBooking?: GroupSizeBooking;
    size?: 'xs' | 'sm' | 'md';
    showRetired?: boolean;
}) => {
    const { t } = useTranslation();

    // Sum of allocated counts (excluding infants as per original sum definition)
    const allocatedSum = adults + children + seniors + guests;

    // Sum of original booking counts
    const originalSum = originalBooking
        ? (originalBooking.adults || 0) +
        (originalBooking.children || 0) +
        (originalBooking.seniors || 0) +
        (originalBooking.guests || 0)
        : allocatedSum;

    const hasDifference = originalBooking && allocatedSum !== originalSum;

    const isXs = size === 'xs';
    const isMd = size === 'md';

    const categoryKeys: ('infants' | 'children' | 'adults' | 'seniors' | 'guests')[] = [
        'infants',
        'children',
        'adults',
        'seniors',
        'guests',
    ];

    const activeCategoriesCount = categoryKeys.filter((key) => {
        const allocated = { adults, children, seniors, infants, guests }[key] || 0;
        const total = originalBooking ? (originalBooking[key] || 0) : allocated;
        return total > 0;
    }).length;

    const shouldShowTotal = activeCategoriesCount > 1;

    const renderCategory = (
        key: 'adults' | 'children' | 'seniors' | 'infants' | 'guests'
    ) => {
        const allocated = { adults, children, seniors, infants, guests }[key] || 0;
        const total = originalBooking ? (originalBooking[key] || 0) : allocated;

        if (total === 0) return null;

        const colors = PAX_CATEGORY_COLORS[key];
        const labelKey = key === 'seniors' && showRetired ? 'retired_plural' : key;

        return (
            <div
                title={t(labelKey)}
                className={`flex items-center ${isXs ? 'gap-0.5' : 'gap-1'} select-none shrink-0`}
            >
                <span className={`
                    rounded-full 
                    font-bold 
                    leading-none 
                    shrink-0 ${colors.bg} ${colors.text}
                    flex items-center justify-center
                    ${isXs ? 'w-3.5 h-3.5 text-[7px]' : isMd ? 'w-5.5 h-5.5 text-[9px]' : 'w-5 h-5 text-[8px]'}
                `}>
                    {t(labelKey).substring(0, 2)}
                </span>
                <span className={` shrink-0 ${isXs ? 'font-semibold text-[10px] text-slate-800' : isMd ? 'font-extrabold text-xs text-slate-800' : 'font-bold text-xs text-gray-600'}`}>
                    {allocated}{originalBooking && (allocated !== total) && <span className="text-gray-400">/{total}</span>}
                </span>
            </div>
        );
    };

    return (
        <div className={`
            ${className}
            flex items-center 
            w-fit shrink-0 
            rounded-full
            pr-1.5
            ${isXs
                ? 'gap-1 bg-slate-100/80 border border-slate-200/80 opacity-85'
                : isMd
                    ? `gap-2 bg-white border 
                    ${hasDifference
                        ? 'border-orange-400'
                        : 'border-slate-300/80'
                    } 
                    shadow-2xs`
                    : `gap-2 bg-white border 
                    ${hasDifference
                        ? 'border-orange-400'
                        : 'border-gray-300'} 
            `}
        `}>
            {renderCategory('infants')}
            {renderCategory('children')}
            {renderCategory('adults')}
            {renderCategory('seniors')}
            {renderCategory('guests')}

            {shouldShowTotal && (
                <div
                    className={`border-l ml-0.5 pl-1 flex items-center gap-0.5 shrink-0 ${isXs ? 'text-[10px] border-slate-200' : isMd ? 'text-xs border-slate-300' : 'text-xs border-gray-200'}`}
                    title={t('total')}
                >
                    <div className="text-gray-400 font-bold">x</div>
                    <span className={`
                    ${isXs
                            ? 'font-bold text-slate-900'
                            : isMd ? 'font-black text-emerald-800 text-sm leading-none'
                                : 'font-extrabold text-green-900'}
                    `}>
                        {allocatedSum}</span>{originalBooking && (allocatedSum !== originalSum) && (
                            <>/<span className="font-bold text-gray-400">{originalSum}</span></>
                        )}
                </div>
            )}
        </div>
    );
};

export const BookingGroupSize = ({
    booking,
    reservation,
    showPaxData = false,
    active = false,
}: {
    booking: GroupSizeBooking & {
        _original_booking?: GroupSizeBooking;
        reservation?: Reservation;
        service_order_pax_groups?: ServiceOrderPaxGroup[];
    }
    reservation?: Reservation
    showPaxData?: boolean
    active?: boolean
}) => {
    const { t } = useTranslation();
    const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string | number, boolean>>({});
    const groupsToRender: (ServiceBookingPaxGroup | ServiceOrderPaxGroup)[] =
        (booking.service_order_pax_groups && booking.service_order_pax_groups.length > 0)
            ? booking.service_order_pax_groups
            : ((booking.pax_groups && booking.pax_groups.length > 0)
                ? booking.pax_groups
                : (booking._original_booking?.pax_groups || []));

    const groupPersons: Person[] = [];
    const seenIds = new Set<number>();
    for (const bg of groupsToRender) {
        if (bg.pax_group?.persons) {
            for (const p of bg.pax_group.persons) {
                if (p.id && !seenIds.has(p.id)) {
                    seenIds.add(p.id);
                    groupPersons.push(p);
                }
            }
        }
    }

    // replace this after migration
    // const show_pax_list = booking?.show_pax_list || false;
    const showPaxList = true;

    let sumAdults = 0;
    let sumChildren = 0;
    let sumInfants = 0;
    let sumSeniors = 0;
    let sumGuests = 0;
    let hasBreakdown = false;
    let assignedTotal = 0;

    if (groupsToRender.length > 0) {
        sumAdults = groupsToRender.reduce((sum, g) => sum + (g.adults ?? 0), 0);
        sumChildren = groupsToRender.reduce((sum, g) => sum + (g.children ?? 0), 0);
        sumInfants = groupsToRender.reduce((sum, g) => sum + (g.infants ?? 0), 0);
        sumSeniors = groupsToRender.reduce((sum, g) => sum + (g.seniors ?? 0), 0);
        sumGuests = groupsToRender.reduce((sum, g) => sum + (g.guests ?? 0), 0);
        assignedTotal = sumAdults + sumChildren + sumSeniors + sumGuests;
        hasBreakdown = true;
    }

    const resToUse = reservation || booking.reservation;
    const clientName = resToUse?.client_business?.name
        || booking.service_order?.client_business?.name
        || booking.service_order_bookings?.[0]?.service_order?.client_business?.name
        || '';

    const hasPaxGroups = Boolean(booking.pax_groups && booking.pax_groups.length > 0);
    const showRetired = Boolean((booking as any).service_data?.show_retired || (booking as any).service?.show_retired);

    const content = (
        <div className={`
            w-fit flex items-start gap-1
        `}>
            {/* Primary: Booking Actual Size */}
            <div className="flex items-center justify-between gap-2">
                <ShowPaxGroup
                    size="md"
                    adults={booking.adults}
                    children={booking.children}
                    seniors={booking.seniors}
                    infants={booking.infants}
                    guests={booking.guests}
                    showRetired={showRetired}
                />
            </div>

            {!hasPaxGroups && (
                <div className="solid-box-border-tinted font-normal text-xs flex items-center gap-1">
                    <IconUsersGroup size={11} className="shrink-0 text-indigo-500" />
                    {t('group')} {clientName}
                </div>
            )}

            {/* Secondary: Linked Pax Groups & Paxs */}
            {hasPaxGroups && (
                <div
                    className={`
                        ${active
                            ? 'solid-box-border-positive'
                            : 'solid-box-border-tinted'
                        }
                    `}
                >
                    {booking.pax_groups?.map((bg: ServiceBookingPaxGroup) => {
                        const groupName = bg.custom_name || bg.pax_group?.name || '';
                        const groupAdults = bg.adults ?? bg.pax_group?.adults ?? 0;
                        const groupChildren = bg.children ?? bg.pax_group?.children ?? 0;
                        const groupSeniors = bg.seniors ?? bg.pax_group?.seniors ?? 0;
                        const groupInfants = bg.infants ?? bg.pax_group?.infants ?? 0;
                        const groupGuests = bg.guests ?? bg.pax_group?.guests ?? 0;

                        const persons: Person[] = resolveBookingPaxGroupPersons(bg);

                        const showGroupSize =
                            persons.length !== 1 ||
                            BookingPaxsSize({
                                adults: groupAdults,
                                children: groupChildren,
                                seniors: groupSeniors,
                                infants: groupInfants,
                                guests: groupGuests,
                            }) !== BookingPaxsSize(persons[0]);

                        return (
                            <div
                                key={bg.id}
                                className="min-w-0 text-[11px]"
                            >
                                <div className="flex flex-wrap gap-1 items-center min-w-0">
                                    {(Boolean(groupName) || showGroupSize) && (
                                        <IconUsersGroup size={11} className="shrink-0 text-indigo-400" />
                                    )}
                                    <span className="font-semibold text-slate-700 break-words" title={groupName}>
                                        {groupName}
                                    </span>
                                    {showGroupSize && (
                                        <ShowPaxGroup
                                            size="xs"
                                            adults={groupAdults}
                                            children={groupChildren}
                                            seniors={groupSeniors}
                                            infants={groupInfants}
                                            guests={groupGuests}
                                            showRetired={showRetired}
                                        />
                                    )}
                                </div>
                                {showPaxList && persons.length > 0 && (() => {
                                    const sorted = sortPersons(persons);
                                    const groupKey = bg.id || bg.pax_group_id || groupName || 'default';
                                    const isExpanded = Boolean(expandedGroupIds[groupKey]);
                                    const visiblePersons = isExpanded ? sorted : sorted.slice(0, 6);
                                    const hiddenCount = Math.max(0, sorted.length - visiblePersons.length);

                                    return (
                                        <div className="ml-1.5 mt-0.5 pl-2 space-y-0.5 border-l border-slate-200">
                                            <div className="space-y-0.25">
                                                {visiblePersons.map((p: Person) => {
                                                    const hasPersonCounts = ((p.adults || 0) + (p.children || 0) + (p.seniors || 0) + (p.infants || 0) + (p.guests || 0)) > 0;
                                                    return (
                                                        <div
                                                            key={p.id || p.document_number || p.name}
                                                            className="flex gap-1 items-baseline text-[11px] text-slate-900"
                                                        >
                                                            <IconUser size={10} className="shrink-0 text-slate-400" />
                                                            <div className="flex flex-wrap gap-1 items-center">
                                                                <div className="break-words">{p.name}</div>
                                                                {hasPersonCounts && (
                                                                    <ShowPaxGroup
                                                                        size="xs"
                                                                        adults={p.adults}
                                                                        children={p.children}
                                                                        seniors={p.seniors}
                                                                        infants={p.infants}
                                                                        guests={p.guests}
                                                                        showRetired={showRetired}
                                                                    />
                                                                )}
                                                                {showPaxData && p.phone && (
                                                                    <div className="font-light text-slate-500 flex items-center gap-0.5 shrink-0">
                                                                        <IconPhone size={9} stroke={1.2} />
                                                                        <span>{p.phone}</span>
                                                                    </div>
                                                                )}
                                                                {showPaxData && p.document_number && (
                                                                    <div className="font-light text-slate-500 flex items-center gap-0.5 shrink-0">
                                                                        <IconId size={9} stroke={1.2} />
                                                                        <span>{p.document_number}</span>
                                                                    </div>
                                                                )}
                                                                {showPaxData && p.email && (
                                                                    <div className="font-light text-slate-500 flex items-center gap-0.5 shrink-0">
                                                                        <IconEmailStamp size={9} stroke={1.2} />
                                                                        <span>{p.email}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {sorted.length > 6 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedGroupIds((prev) => ({
                                                                ...prev,
                                                                [groupKey]: !prev[groupKey],
                                                            }));
                                                        }}
                                                        className="px-1.5 py-0.5 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-bold text-[10px] cursor-pointer transition-colors"
                                                    >
                                                        {isExpanded ? t('show_less') : `+${hiddenCount}`}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    return content;
};

export const BookingGroupSizeSlim = ({
    booking,
}: {
    booking: GroupSizeBooking
}) => {
    return (
        <div>
            <ShowPaxGroup
                adults={booking.adults}
                children={booking.children}
                seniors={booking.seniors}
                infants={booking.infants}
                guests={booking.guests}
            />
        </div>
    );
};


export const MoneyIn = forwardRef<HTMLDivElement, { amount: number, currency: string, className?: string }>(({ amount, currency, className }, ref) => {
    return (
        <div ref={ref} key={currency} className={`flex gap-0.5 justify-end ${className}`}>
            <div className="text-gray-400 font-light">$</div>
            <div className="font-bold">{formatPrice(amount)}</div>
            <div className="text-gray-600 font-medium">{currency}</div>
        </div>
    );
});

export const MoneyOut = ({ amount, currency }: { amount: number, currency: string }) => {
    return (
        <div key={currency} className="flex gap-0.5 justify-end opacity-70 text-sm">
            <div className="text-gray-400">$</div>
            <div>{formatPrice(amount) || '-'}</div>
            <div className="text-gray-400">{amount}</div>
        </div>
    );
}

export const MoneyInInput = ({ amount, currency, onChange, onChange2, label = 'money_in' }: { amount: number, currency: string, onChange: (value: number) => void, onChange2: (value: string) => void, label?: string }) => {
    const { t } = useTranslation();
    return (
        <PriceCurrencyInput
            label={t(label)}
            value={amount}
            onChange={(value) => onChange(value)}
            value2={currency}
            onChange2={(value) => onChange2(value)}
            icon={<IconCurrencyDollar opacity="0.5" size={16} stroke={2} color='green' />}
        />
    );
}

export const MoneyNumberInput = ({
    amount,
    onChange,
    label,
    className,
    size = 'sm',
    icon = null as React.ReactNode | null,
    disabled = false,
    error,
    rightSection = null as React.ReactNode | null,
}:
    {
        amount?: number | null,
        onChange: (value: any) => void,
        label?: string,
        className?: string,
        size?: 'sm' | 'md' | 'lg',
        icon?: React.ReactNode | null,
        disabled?: boolean,
        error?: React.ReactNode,
        rightSection?: React.ReactNode | null,
    }) => (
    <NumberInput
        size={size}
        label={label}
        className={className}
        hideControls
        leftSection={icon || <IconCurrencyDollar size={16} stroke={1.5} color="#bbb" />}
        rightSection={rightSection}
        thousandSeparator="."
        value={amount === null || amount === undefined ? '' : amount}
        onChange={(value) => {
            if (value === '' || value === null || value === undefined) {
                onChange(null);
            } else {
                const num = Number(value);
                onChange(isNaN(num) ? null : num);
            }
        }}
        // decimalScale={2}
        decimalSeparator=","
        disabled={disabled}
        error={error}
    />
)

export const MoneyOutInput = ({ amount, currency, onChange, onChange2 }: { amount: number, currency: string, onChange: (value: number) => void, onChange2: (value: string) => void }) => {
    const { t } = useTranslation();
    return (
        <PriceCurrencyInput
            label={t('money_out')}
            value={amount}
            onChange={(value) => onChange(value)}
            value2={currency}
            onChange2={(value) => onChange2(value)}
            icon={<IconCurrencyDollar opacity="0.5" size={16} stroke={2} color='red' />}
        />
    );
}

export const MoneyOutInputDisabled = ({ amount, currency }: { amount: number, currency: string }) => {
    const { t } = useTranslation();
    return (
        <PriceCurrencyInput
            disabled={true}
            label={t('money_out')}
            value={amount}
            onChange={() => { }}
            value2={currency}
            onChange2={() => { }}
            icon={<IconCurrencyDollar opacity="0.5" size={16} stroke={2} color='red' />}
        />
    );
}


export const SkeletonList = ({ height = 40 }: { height?: number }) => {
    return (
        <div className="flex flex-col gap-1 mt-1">
            <Skeleton height={height} width="100%" radius="sm" />
            <Skeleton height={height} width="100%" radius="sm" />
            <Skeleton height={height} width="90%" radius="sm" />
            <Skeleton height={height} width="80%" radius="sm" />
        </div>
    );
}

export const SkeletonListWithFilters = ({ height = 40 }: { height?: number }) => {
    return (
        <div className="flex gap-4 w-full mt-1">
            <div className="w-1/4 space-y-1 h-fit">
                <Skeleton height={height} width="100%" radius="sm" />
                <Skeleton height={height * 3} width="100%" radius="sm" />
            </div>
            <div className="space-y-1 w-3/4 h-fit">
                <Skeleton height={height} width="100%" radius="sm" />
                <Skeleton height={height} width="100%" radius="sm" />
                <Skeleton height={height} width="90%" radius="sm" />
                <Skeleton height={height} width="80%" radius="sm" />
            </div>
        </div>
    );
}

export const ExternalCreatedByIndicator = ({
    createdBy,
}: {
    createdBy: {
        tenant_id?: number | null;
        tenant_name?: string | null;
        name?: string | null;
    } | null | undefined;
}) => {
    const { t } = useTranslation();
    const tenant = localTenant();

    if (!createdBy?.tenant_id || createdBy.tenant_id === tenant?.id) {
        return null;
    }

    const label = `${t('created_by')}: ${createdBy.name || ''} (${createdBy.tenant_name || ''})`;

    return (
        <Tooltip label={label} withArrow position="top">
            <IconUserShare color='purple' stroke={3} size={14} className="cursor-help shrink-0" />
        </Tooltip>
    );
};

export const getPaymentMethodIcon = (method: string, size: number = 16) => {
    switch (method) {
        case 'bank_transfer':
            return <IconBuildingBank size={size} />;
        case 'card':
            return <IconCreditCard size={size} />;
        case 'cash':
            return <IconCash size={size} />;
        case 'paypal':
            return <IconBrandPaypal size={size} />;
        case 'stripe':
            return <IconBrandStripe size={size} />;
        case 'wallet_transfer':
            return <IconWallet size={size} />;
        case 'service_reciprocity':
        case 'reconciling':
            return <IconArrowsExchange size={size} />;
        default:
            return <IconCreditCard size={size} />;
    }
};

export const PaymentMethodBadge = ({ method }: { method: string }) => {
    const { t } = useTranslation();
    const iconSize = 10;

    const config: Record<string, { label: string; colorClass: string }> = {
        bank_transfer: {
            label: t('payment_methods.bank_transfer'),
            colorClass: 'bg-blue-50 text-blue-700 border-blue-100/70 hover:bg-blue-100/50'
        },
        card: {
            label: t('payment_methods.card'),
            colorClass: 'bg-indigo-50 text-indigo-700 border-indigo-100/70 hover:bg-indigo-100/50'
        },
        cash: {
            label: t('payment_methods.cash'),
            colorClass: 'bg-emerald-50 text-emerald-700 border-emerald-100/70 hover:bg-emerald-100/50'
        },
        paypal: {
            label: t('payment_methods.paypal'),
            colorClass: 'bg-sky-50 text-sky-700 border-sky-100/70 hover:bg-sky-100/50'
        },
        stripe: {
            label: t('payment_methods.stripe'),
            colorClass: 'bg-purple-50 text-purple-700 border-purple-100/70 hover:bg-purple-100/50'
        },
        wallet_transfer: {
            label: t('payment_methods.wallet_transfer'),
            colorClass: 'bg-teal-50 text-teal-700 border-teal-100/70 hover:bg-teal-100/50'
        },
        service_reciprocity: {
            label: t('payment_methods.service_reciprocity'),
            colorClass: 'bg-amber-50 text-amber-700 border-amber-100/70 hover:bg-amber-100/50'
        },
        reconciling: {
            label: t('payment_methods.reconciling'),
            colorClass: 'bg-slate-50 text-slate-700 border-slate-100/70 hover:bg-slate-100/50'
        }
    };

    const item = config[method] || {
        label: t(`payment_methods.${method}`, method),
        colorClass: 'bg-gray-50 text-gray-700 border-gray-100/70 hover:bg-gray-100/50'
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] border transition-colors ${item.colorClass}`}>
            {getPaymentMethodIcon(method, iconSize)}
            <span>{item.label}</span>
        </span>
    );
};

export const PaymentMethodSelect = ({
    value,
    onChange,
    label,
    className,
    disabled
}: {
    value: string | null | undefined;
    onChange: (value: string | null) => void;
    label?: string;
    className?: string;
    disabled?: boolean;
}) => {
    const { t } = useTranslation();

    const data = getPaymentMethods(t);

    const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => {
        return (
            <Group flex="1" gap="xs">
                {getPaymentMethodIcon(option.value, 16)}
                {option.label}
                {checked && <IconCheck size={16} style={{ marginInlineStart: 'auto' }} />}
            </Group>
        );
    };

    return (
        <Select
            label={label ?? t('method')}
            data={data}
            value={value}
            onChange={onChange}
            disabled={disabled}
            searchable
            allowDeselect
            className={className}
            renderOption={renderSelectOption}
            leftSection={value ? getPaymentMethodIcon(value, 16) : null}
            leftSectionPointerEvents="none"
        />
    );
};

export const PaxCategoryBadge = ({
    category,
    className = '',
}: {
    category?: string | null;
    className?: string;
}) => {
    const { t } = useTranslation();
    const colors = category ? PAX_CATEGORY_COLORS[category] : undefined;

    return (
        <span className={`
            rounded-full text-[8px] 
            font-bold
            leading-none 
            shrink-0 ${colors?.bg || 'bg-slate-100'} ${colors?.text || 'text-slate-400'}
            w-5 h-5 flex items-center justify-center ${className}
        `}>
            {category ? t(category).substring(0, 2) : '--'}
        </span>
    );
};

export type PersonCategory = (typeof PERSON_CATEGORIES)[keyof typeof PERSON_CATEGORIES];

export const PaxCategorySelect = ({
    value,
    onChange,
    label,
    size = 'xs',
    className = '',
    disabled = false,
    clearable = false,
    onFocus,
}: {
    value: PersonCategory | string | undefined | null;
    onChange: (value: any) => void;
    label?: string;
    size?: MantineSize;
    className?: string;
    disabled?: boolean;
    clearable?: boolean;
    onFocus?: () => void;
}) => {
    const { t } = useTranslation();
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const personCategories = Object.values(PERSON_CATEGORIES);
    const selectedCategory = value && (personCategories as readonly string[]).includes(value)
        ? (value as PersonCategory)
        : null;

    return (
        <Combobox
            store={combobox}
            withinPortal={true}
            disabled={disabled}
            onOptionSubmit={(val) => {
                if (clearable && selectedCategory === val) {
                    onChange(null);
                } else {
                    onChange(val as PersonCategory);
                }
                combobox.closeDropdown();
            }}
        >
            <Combobox.Target>
                <InputBase
                    component="button"
                    type="button"
                    pointer
                    label={label}
                    size={size}
                    disabled={disabled}
                    className={className}
                    rightSection={
                        <Combobox.Chevron />
                    }
                    onClick={() => {
                        onFocus?.();
                        combobox.toggleDropdown();
                    }}
                >
                    {selectedCategory ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                            <TagIcon name={selectedCategory} hideText size="xxs" />
                            <span className="text-xs font-medium text-slate-800">
                                {t(selectedCategory)}
                            </span>
                        </div>
                    ) : (
                        <Input.Placeholder>
                            {t('category')}
                        </Input.Placeholder>
                    )}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown data-combobox-dropdown>
                <Combobox.Options>
                    {personCategories.map((cat) => {
                        const isSelected = selectedCategory === cat;
                        return (
                            <Combobox.Option value={cat} key={cat} active={isSelected}>
                                <div className="flex items-center justify-between gap-2 w-full">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <TagIcon name={cat} hideText size="xxs" />
                                        <span className="text-xs font-medium text-slate-800">
                                            {t(cat)}
                                        </span>
                                    </div>
                                    {isSelected && <IconCheck size={14} className="text-slate-600 shrink-0" />}
                                </div>
                            </Combobox.Option>
                        );
                    })}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

export const ShowPerson = ({
    person,
    className = '',
    className2 = '',
    hideIcon = false,
    showPaxData = true,
}: {
    person: Person;
    className?: string;
    className2?: string;
    hideIcon?: boolean;
    showPaxData?: boolean;
}) => {
    const hasPaxCounts =
        (person.adults || 0) +
        (person.children || 0) +
        (person.seniors || 0) +
        (person.infants || 0) +
        (person.guests || 0) > 0;

    return (
        <div className="min-w-0 flex flex-col gap-0.5">
            {/* Line 1: User icon + Name + Pax badge + Contact info */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
                <div className="inline-flex items-center gap-1 min-w-0 max-w-full">
                    <TagIcon name={person.category} hideText size="xxs" noBackground />
                    <span
                        className={`max-w-[180px] sm:max-w-[240px] ${className || 'font-medium text-slate-800 text-xxs'}`}
                        title={person.name}
                    >
                        {person.name}
                    </span>
                    {hasPaxCounts && (
                        <ShowPaxGroup
                            size="xs"
                            adults={person.adults}
                            children={person.children}
                            seniors={person.seniors}
                            infants={person.infants}
                            guests={person.guests}
                        />
                    )}
                </div>

                {showPaxData && person.phone && (
                    <div className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 font-mono shrink-0 leading-none">
                        <IconPhone size={10} className="text-slate-400 shrink-0" />
                        <span>{person.phone}</span>
                    </div>
                )}
                {showPaxData && person.phone_2 && (
                    <div className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 font-mono shrink-0 leading-none">
                        <IconPhone size={10} className="text-slate-400 shrink-0" />
                        <span>{person.phone_2}</span>
                    </div>
                )}
                {showPaxData && person.email && (
                    <div
                        className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 min-w-0 max-w-[160px] leading-none"
                        title={person.email}
                    >
                        <IconMail size={10} className="text-slate-400 shrink-0" />
                        <span>{person.email}</span>
                    </div>
                )}
            </div>

            {/* Line 2: Observations */}
            {person.observations && (
                <Tooltip
                    label={person.observations}
                    multiline
                    maw={280}
                    withArrow
                    position="bottom-start"
                    openDelay={300}
                    disabled={person.observations.length < 35}
                >
                    <div
                        className={`${!hideIcon ? 'pl-1' : ''} leading-tight max-w-full ${className2 || 'font-light text-slate-500 text-[10px] italic'}`}
                    >
                        {person.observations}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};